import React, { useMemo } from 'react';
import { Text, Group } from 'react-konva';

import FlashbackLayout from 'common/components/RewindModal/Konvas/FlashbackLayout';
import KonvaImage from 'common/components/RewindModal/Konvas/KonvaImage';
import ShareableStage from 'common/components/RewindModal/Konvas/ShareableStage';
import {
  LAYER_WIDTH,
  LAYER_HEIGHT,
  BLOCK_MARGIN,
  FLASHBACK_COVER_LOGO_WIDTH,
  FLASHBACK_COVER_LOGO_HEIGHT,
  FLASHBACK_COVER_LOGO_TOP_MARGIN,
  FLASHBACK_COVER_LOGO_OPACITY,
  ALLOCINE_LOGO_WIDTH,
  ALLOCINE_LOGO_HEIGHT,
  ALLOCINE_LOGO_BOTTOM_MARGIN,
  FLASHBACK_BACKGROUND_COLOR,
  FLASHBACK_TEXT_COLOR,
  FLASHBACK_FONT_FAMILY,
  FLASHBACK_BILAN_HOURS_FONT_SIZE,
  FLASHBACK_BILAN_HOURS_FONT_WEIGHT,
  FLASHBACK_BILAN_HOURS_LINE_HEIGHT,
  FLASHBACK_BILAN_HOURS_TEXT_FONT_SIZE,
  FLASHBACK_BILAN_HOURS_TEXT_FONT_WEIGHT,
  FLASHBACK_BILAN_HOURS_TEXT_LINE_HEIGHT,
  FLASHBACK_BILAN_POLAROID_WIDTH,
  FLASHBACK_BILAN_POLAROID_HEIGHT,
  FLASHBACK_COMPARISON_BADGE_WIDTH,
  FLASHBACK_COMPARISON_BADGE_HEIGHT,
  FLASHBACK_COMPARISON_BADGE_ROTATION,
  FLASHBACK_COMPARISON_BADGE_OFFSET_X,
  FLASHBACK_COMPARISON_BADGE_OFFSET_Y,
  FLASHBACK_BILAN_MEMBER_CARD_WIDTH,
  FLASHBACK_BILAN_MEMBER_CARD_HEIGHT,
  FLASHBACK_BILAN_MEMBER_CARD_ROTATION,
  FLASHBACK_BILAN_POLAROID_BADGE_WIDTH,
  FLASHBACK_BILAN_POLAROID_BADGE_HEIGHT,
  FLASHBACK_BILAN_POLAROID_BADGE_ROTATION,
  FLASHBACK_BILAN_POLAROID_BADGE_OFFSET_X,
  FLASHBACK_BILAN_POLAROID_BADGE_OFFSET_Y,
  FLASHBACK_BILAN_THANKS_BADGE_WIDTH,
  FLASHBACK_BILAN_THANKS_BADGE_HEIGHT,
  FLASHBACK_BILAN_THANKS_BADGE_ROTATION,
  FLASHBACK_BILAN_THANKS_BADGE_OFFSET_X,
  FLASHBACK_BILAN_THANKS_BADGE_OFFSET_Y,
  FLASHBACK_BILAN_HOURS_TO_TEXT_SPACING,
  FLASHBACK_BILAN_TEXT_TO_IMAGES_SPACING,
  FLASHBACK_BILAN_IMAGES_SPACING,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_ROTATION,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_FONT_SIZE,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_FONT_STYLE,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_LINE_HEIGHT,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_OFFSET_X,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_OFFSET_Y,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_OFFSET_Y_EXTRA,
  FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_FONT_FAMILY
} from 'common/components/RewindModal/Konvas/constants';
import { getDPRSize } from 'common/components/RewindModal/helper';
import {
  MEMBER_CARD_MOVIE,
  MEMBER_CARD_SERIES,
  MEMBER_CARD_SERIES_MOVIE
} from 'common/configuration/constants';

type ScreenBilanUserProps = {
  data: {
    total_runtime_hours?: string;
    image_runtime?: string;
    movie_count?: number;
    series_count?: number;
    image_comparison?: string;
    image_favorite_actor?: string;
    image_user_ranking?: string;
  };
  userNickname?: string;
};

const ShareableScreenBilanUser = React.forwardRef<any, ScreenBilanUserProps>(
  ({ data, userNickname }, ref) => {
    const hours = data?.total_runtime_hours ?? '';
    const movieCount = data?.movie_count ?? 0;
    const seriesCount = data?.series_count ?? 0;

    const memberType =
      movieCount > seriesCount
        ? 'movie'
        : seriesCount > movieCount
          ? 'series'
          : 'seriemovie';
    const memberCardImage =
      memberType === 'movie'
        ? MEMBER_CARD_MOVIE
        : memberType === 'series'
          ? MEMBER_CARD_SERIES
          : MEMBER_CARD_SERIES_MOVIE;

    const imagePaths = useMemo(() => {
      const buildPath = (imageName: string | null | undefined) => {
        if (!imageName) return undefined;
        return `/commons/v9/allocine/flashback/${imageName}.png`;
      };

      return {
        runtime: buildPath(data?.image_runtime),
        comparison: buildPath(data?.image_comparison),
        favoriteActor: buildPath(data?.image_favorite_actor),
        userRanking: buildPath(data?.image_user_ranking)
      };
    }, [data]);

    const textLayout = useMemo(() => {
      const hoursHeight =
        FLASHBACK_BILAN_HOURS_FONT_SIZE * FLASHBACK_BILAN_HOURS_LINE_HEIGHT;

      const y_hours = 0;
      const y_text =
        y_hours + hoursHeight + FLASHBACK_BILAN_HOURS_TO_TEXT_SPACING;

      return { y_hours, y_text };
    }, []);

    const layout = useMemo(() => {
      const textBlockHeight =
        textLayout.y_text +
        FLASHBACK_BILAN_HOURS_TEXT_FONT_SIZE *
          FLASHBACK_BILAN_HOURS_TEXT_LINE_HEIGHT;

      const topMargin =
        FLASHBACK_COVER_LOGO_TOP_MARGIN +
        FLASHBACK_COVER_LOGO_HEIGHT +
        BLOCK_MARGIN * 2;

      const y_images =
        topMargin + textBlockHeight + FLASHBACK_BILAN_TEXT_TO_IMAGES_SPACING;
      const y_polaroid = y_images;

      return { topMargin, y_polaroid };
    }, [textLayout]);

    const badgePositions = useMemo(() => {
      const centerX = LAYER_WIDTH / 2;
      const cardX = centerX - FLASHBACK_BILAN_POLAROID_WIDTH / 2;
      const cardY = 0;

      const marginForRotationX =
        (FLASHBACK_COMPARISON_BADGE_WIDTH * Math.sqrt(2) -
          FLASHBACK_COMPARISON_BADGE_WIDTH) /
        2;
      const marginForRotationY =
        (FLASHBACK_COMPARISON_BADGE_HEIGHT * Math.sqrt(2) -
          FLASHBACK_COMPARISON_BADGE_HEIGHT) /
        2;

      const badgeCenterXBase =
        cardX +
        FLASHBACK_BILAN_POLAROID_WIDTH -
        FLASHBACK_COMPARISON_BADGE_WIDTH / 2 +
        FLASHBACK_COMPARISON_BADGE_OFFSET_X +
        getDPRSize(180);

      const badgeCenterX = Math.min(
        badgeCenterXBase,
        LAYER_WIDTH -
          FLASHBACK_COMPARISON_BADGE_WIDTH / 2 -
          marginForRotationX -
          BLOCK_MARGIN +
          getDPRSize(20)
      );

      const badgeCenterYBase =
        cardY +
        FLASHBACK_COMPARISON_BADGE_HEIGHT / 2 +
        FLASHBACK_COMPARISON_BADGE_OFFSET_Y -
        getDPRSize(30);

      const maxYRelativeToGroup =
        LAYER_HEIGHT -
        layout.topMargin -
        FLASHBACK_COMPARISON_BADGE_HEIGHT / 2 -
        marginForRotationY -
        ALLOCINE_LOGO_HEIGHT -
        ALLOCINE_LOGO_BOTTOM_MARGIN;

      const badgeCenterY = Math.min(badgeCenterYBase, maxYRelativeToGroup);

      return { badgeCenterX, badgeCenterY };
    }, [layout.topMargin]);

    const centerX = LAYER_WIDTH / 2;

    return (
      <ShareableStage ref={ref}>
        <FlashbackLayout
          backgroundColor={FLASHBACK_BACKGROUND_COLOR}
          backgroundOpacity={1}
          coverLogoWidth={FLASHBACK_COVER_LOGO_WIDTH}
          coverLogoHeight={FLASHBACK_COVER_LOGO_HEIGHT}
          coverLogoTopMargin={FLASHBACK_COVER_LOGO_TOP_MARGIN}
          coverLogoOpacity={FLASHBACK_COVER_LOGO_OPACITY}
          allocineLogoWidth={ALLOCINE_LOGO_WIDTH}
          allocineLogoHeight={ALLOCINE_LOGO_HEIGHT}
          allocineLogoBottomMargin={ALLOCINE_LOGO_BOTTOM_MARGIN}
        >
          <Group y={layout.topMargin}>
            {hours && (
              <Group>
                <Text
                  text={`${hours} h`}
                  fontSize={FLASHBACK_BILAN_HOURS_FONT_SIZE}
                  fontFamily={FLASHBACK_FONT_FAMILY}
                  fontStyle={FLASHBACK_BILAN_HOURS_FONT_WEIGHT}
                  lineHeight={FLASHBACK_BILAN_HOURS_LINE_HEIGHT}
                  width={LAYER_WIDTH}
                  align="center"
                  y={textLayout.y_hours}
                  fill={FLASHBACK_TEXT_COLOR}
                />
                <Text
                  text="de visionnage"
                  fontSize={FLASHBACK_BILAN_HOURS_TEXT_FONT_SIZE}
                  fontFamily={FLASHBACK_FONT_FAMILY}
                  fontStyle={FLASHBACK_BILAN_HOURS_TEXT_FONT_WEIGHT}
                  lineHeight={FLASHBACK_BILAN_HOURS_TEXT_LINE_HEIGHT}
                  width={LAYER_WIDTH}
                  align="center"
                  y={textLayout.y_text}
                  fill={FLASHBACK_TEXT_COLOR}
                />
              </Group>
            )}

            <Group y={layout.y_polaroid - layout.topMargin}>
              {imagePaths.runtime && (
                <Group>
                  <KonvaImage
                    path={imagePaths.runtime}
                    x={centerX - FLASHBACK_BILAN_POLAROID_WIDTH / 2}
                    y={0}
                    width={FLASHBACK_BILAN_POLAROID_WIDTH}
                    height={FLASHBACK_BILAN_POLAROID_HEIGHT}
                  />
                  {imagePaths.comparison && (
                    <KonvaImage
                      path={imagePaths.comparison}
                      isLocal={false}
                      x={badgePositions.badgeCenterX}
                      y={badgePositions.badgeCenterY}
                      width={FLASHBACK_COMPARISON_BADGE_WIDTH}
                      height={FLASHBACK_COMPARISON_BADGE_HEIGHT}
                      rotation={FLASHBACK_COMPARISON_BADGE_ROTATION}
                      offsetX={FLASHBACK_COMPARISON_BADGE_WIDTH / 2}
                      offsetY={FLASHBACK_COMPARISON_BADGE_HEIGHT / 2}
                    />
                  )}
                </Group>
              )}

              <Group
                x={centerX}
                y={
                  FLASHBACK_BILAN_POLAROID_HEIGHT +
                  FLASHBACK_BILAN_IMAGES_SPACING
                }
                offsetX={FLASHBACK_BILAN_MEMBER_CARD_WIDTH / 2}
                offsetY={0}
              >
                <Group rotation={FLASHBACK_BILAN_MEMBER_CARD_ROTATION}>
                  <KonvaImage
                    path={memberCardImage}
                    isLocal={true}
                    width={FLASHBACK_BILAN_MEMBER_CARD_WIDTH}
                    height={FLASHBACK_BILAN_MEMBER_CARD_HEIGHT}
                  />
                  <Text
                    text={userNickname}
                    fontSize={
                      FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_FONT_SIZE
                    }
                    fontFamily={
                      FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_FONT_FAMILY
                    }
                    fontStyle={
                      FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_FONT_STYLE
                    }
                    lineHeight={
                      FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_LINE_HEIGHT
                    }
                    fill="#000000"
                    x={FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_OFFSET_X}
                    y={
                      FLASHBACK_BILAN_MEMBER_CARD_HEIGHT -
                      FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_OFFSET_Y +
                      FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_OFFSET_Y_EXTRA
                    }
                    rotation={
                      FLASHBACK_REPARTITION_MEMBER_CARD_NICKNAME_ROTATION
                    }
                  />
                </Group>

                {imagePaths.favoriteActor && (
                  <Group
                    x={
                      FLASHBACK_BILAN_MEMBER_CARD_WIDTH / 2 +
                      FLASHBACK_BILAN_POLAROID_BADGE_OFFSET_X
                    }
                    y={FLASHBACK_BILAN_POLAROID_BADGE_OFFSET_Y}
                    rotation={FLASHBACK_BILAN_POLAROID_BADGE_ROTATION}
                    offsetX={FLASHBACK_BILAN_POLAROID_BADGE_WIDTH / 2}
                    offsetY={0}
                  >
                    <KonvaImage
                      path={imagePaths.favoriteActor}
                      width={FLASHBACK_BILAN_POLAROID_BADGE_WIDTH}
                      height={FLASHBACK_BILAN_POLAROID_BADGE_HEIGHT}
                    />
                  </Group>
                )}

                {imagePaths.userRanking && (
                  <Group
                    x={
                      FLASHBACK_BILAN_MEMBER_CARD_WIDTH / 2 +
                      FLASHBACK_BILAN_THANKS_BADGE_OFFSET_X
                    }
                    y={FLASHBACK_BILAN_THANKS_BADGE_OFFSET_Y}
                    rotation={FLASHBACK_BILAN_THANKS_BADGE_ROTATION}
                    offsetX={FLASHBACK_BILAN_THANKS_BADGE_WIDTH / 2}
                    offsetY={0}
                  >
                    <KonvaImage
                      path={imagePaths.userRanking}
                      width={FLASHBACK_BILAN_THANKS_BADGE_WIDTH}
                      height={FLASHBACK_BILAN_THANKS_BADGE_HEIGHT}
                    />
                  </Group>
                )}
              </Group>
            </Group>
          </Group>
        </FlashbackLayout>
      </ShareableStage>
    );
  }
);

ShareableScreenBilanUser.displayName = 'ShareableScreenBilanUser';
export default ShareableScreenBilanUser;
